import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CircularChip from '~/Common-Objects/UI/Chips/CircularChip.jsx'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import MultiAccordian from '~/Common-Objects/UI/Accordian/MultiAccordian'
import i18n from '~/i18n'
import EmailValidator from '~/Classes/emailValidator'
import axios from '~/data/http/axios'

import ProviderInstructions from './part1_providerInstructions'
import SPFDKIM from './part2_spfdkim'
import DMARC from './part3_dmarc'
import Tracking from './part4_tracking'
import CDN from './part5_cdn'

import moment from 'moment'

export default function SetupInstructions({
    setFlowStep,
    currentStep = 'dnsInstructions',
    domainProvider = {},
    domain = {},
    domainRecords = [],
    setDomainRecords = () => {},
    updateDomain = () => {},
    subDomain = '',
    finish = () => {}
}) {
    const site = useSelector((state) => state.siteMaster)
    const [stepData, setStepData] = useState([])
    const [renderedStep, setRenderedStep] = useState('dnsInstructions')
    const [newDomainsToAdd, setNewDomainsToAdd] = useState(0)
    const [oldDomainsToUpdate, setOldDomainsToUpdate] = useState(0)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        generateStepData()
    }, [])

    useEffect(() => {
        generateStepData()
    }, [currentStep])

    const finishedFlow = async () => {
        setLoading(true)
        await updateDomain({
            options: {
                doneDnsInstructions: true,
                lastAuthenticationCheckDate: moment().toDate()
            }
        })
        await axios.put(`/accountMaster/domain/${domain.id}/finishDomainSetup`)
        setFlowStep('verifySetup')
    }

    const checkDnsSetup = () => {
        let dnsSetup = {
            doneAuthentication: false,
            doneDmarc: false,
            doneTracking: false,
            doneCdn: false
        }
        if (domain.authenticated) {
            dnsSetup.doneAuthentication = true
        }
        if (domain.dmarc) {
            dnsSetup.doneDmarc = true
        }
        if (domain.trackingDomain?.authenticated) {
            dnsSetup.doneTracking = true
        }
        if (domain.cdnDomain?.authenticated) {
            dnsSetup.doneCdn = true
        }
        return dnsSetup
    }

    const checkNextFlowStep = (nextStep) => {
        //check we have a step to go to
        //we should use an array outside of this to store completed steps and tidy this up in next slice
        //can use to check below for next step stuff too
        const dnsSetup = checkDnsSetup()
        let canFinish = false

        switch (nextStep) {
            case 'spfDkimSettings':
                if (dnsSetup.doneAuthentication && dnsSetup.doneDmarc && dnsSetup.doneTracking && dnsSetup.doneCdn) {
                    canFinish = true
                }
                break
            case 'dmarcSettings':
                if (dnsSetup.doneDmarc && dnsSetup.doneTracking && dnsSetup.doneCdn) {
                    canFinish = true
                }
                break
            case 'trackingSettings':
                if (dnsSetup.doneTracking && dnsSetup.doneCdn) {
                    canFinish = true
                }
                break
            default:
                canFinish = false
                break
        }
        if (canFinish) {
            finishedFlow()
        } else {
            setFlowStep(nextStep)
        }
    }

    const generateStepData = () => {
        const tmpStepData = [
            {
                header: (
                    <div>
                        <CircularChip className='circularChip-smallText circularChip-lightPurple mar-r15'>
                            1
                        </CircularChip>
                        Access Your {domainProvider?.label} DNS Settings
                    </div>
                ),
                content: (
                    <ProviderInstructions
                        domainId={domain.id}
                        setFlowStep={checkNextFlowStep}
                        domainProvider={domainProvider}
                        domainRecords={domainRecords}
                        setDomainRecords={setDomainRecords}
                    />
                ),
                id: 'dnsInstructions',
                completed: false
            },
            {
                header: (
                    <div>
                        <CircularChip className='circularChip-smallText circularChip-lightPurple mar-r15'>
                            2
                        </CircularChip>
                        Setup SPF and DKIM Records
                    </div>
                ),
                content: (
                    <SPFDKIM
                        subDomain={subDomain}
                        domainId={domain.id}
                        domain={domain}
                        setFlowStep={checkNextFlowStep}
                        domainProvider={domainProvider}
                        domainRecords={domainRecords}
                        setDomainRecords={setDomainRecords}
                    />
                ),
                completed: false,
                id: 'spfDkimSettings'
            },
            {
                header: (
                    <div>
                        <CircularChip className='circularChip-smallText circularChip-lightPurple mar-r15'>
                            3
                        </CircularChip>
                        Setup your DMARC Record
                    </div>
                ),
                content: (
                    <DMARC
                        subDomain={subDomain}
                        domainId={domain.id}
                        domain={domain}
                        setFlowStep={checkNextFlowStep}
                        domainProvider={domainProvider}
                        domainRecords={domainRecords}
                        setDomainRecords={setDomainRecords}
                    />
                ),
                id: 'dmarcSettings',
                completed: false
            },
            {
                header: (
                    <div>
                        <CircularChip className='circularChip-smallText circularChip-lightPurple mar-r15'>
                            4
                        </CircularChip>
                        Setup a Tracking Subdomain
                    </div>
                ),
                content: (
                    <Tracking
                        finish={finish}
                        subDomain={subDomain}
                        domain={domain}
                        setFlowStep={checkNextFlowStep}
                        updateDomain={updateDomain}
                        domainProvider={domainProvider}
                        domainRecords={domainRecords}
                        setDomainRecords={setDomainRecords}
                        finishedFlow={finishedFlow}
                    />
                ),
                id: 'trackingSettings',
                completed: false
            },
            {
                header: (
                    <div>
                        <CircularChip className='circularChip-smallText circularChip-lightPurple mar-r15'>
                            5
                        </CircularChip>
                        Setup a CDN Subdomain
                    </div>
                ),
                content: (
                    <CDN
                        finish={finish}
                        finishedFlow={finishedFlow}
                        subDomain={subDomain}
                        domain={domain}
                        updateDomain={updateDomain}
                        domainId={domain.id}
                        setFlowStep={checkNextFlowStep}
                        domainProvider={domainProvider}
                        domainRecords={domainRecords}
                        setDomainRecords={setDomainRecords}
                    />
                ),
                id: 'cdnSettings',
                completed: false
            }
        ]

        if (currentStep == 'spfDkimSettings') tmpStepData[0].completed = true
        if (currentStep == 'dmarcSettings') {
            tmpStepData[0].completed = true
            tmpStepData[1].completed = true
        }
        if (currentStep == 'trackingSettings') {
            tmpStepData[0].completed = true
            tmpStepData[1].completed = true
            tmpStepData[2].completed = true
        }
        if (currentStep == 'cdnSettings') {
            tmpStepData[0].completed = true
            tmpStepData[1].completed = true
            tmpStepData[2].completed = true
            tmpStepData[3].completed = true
        }
        if (domain?.options?.doneDnsInstructions) {
            tmpStepData[0].completed = true
        }
        let domainsToAdd = 0
        let domainsToUpdate = 0
        if (!domain.authenticated) {
            domainsToAdd++
            if (domain.spfAdded) {
                domainsToUpdate++
            } else {
                domainsToAdd++
            }
        } else {
            tmpStepData[1].completed = true
        }
        if (domain.dmarc) {
            tmpStepData[2].completed = true
        } else {
            domainsToAdd++
        }
        if (domain.trackingDomain?.authenticated) {
            tmpStepData[3].completed = true
        } else {
            domainsToAdd++
        }
        if (domain.cdnDomain?.authenticated) {
            tmpStepData[4].completed = true
        } else {
            domainsToAdd++
        }

        let firstNonCompleted = tmpStepData.find((step) => !step.completed)
        setStepData(tmpStepData)
        setNewDomainsToAdd(domainsToAdd)
        setOldDomainsToUpdate(domainsToUpdate)
        if (firstNonCompleted) {
            setRenderedStep(firstNonCompleted.id)
        }
    }

    if (loading) {
        return <LoadingIcons iconType='2' />
    }

    return (
        <div>
            <h2 className='mar-b10 text-center'>Add DNS Records</h2>
            <div className='mar-b40 text-center text-grey-800' style={{ lineHeight: '1.5' }}>
                {newDomainsToAdd > 0 &&
                    oldDomainsToUpdate > 0 &&
                    i18n.t('company:domains.domainManagement.nowNeedToAddHeader', {
                        newDomains: newDomainsToAdd,
                        oldDomains: oldDomainsToUpdate
                    })}
                {newDomainsToAdd > 0 &&
                    oldDomainsToUpdate == 0 &&
                    i18n.t('company:domains.domainManagement.nowNeedToAddHeaderNew', { newDomains: newDomainsToAdd })}
                {newDomainsToAdd == 0 &&
                    oldDomainsToUpdate > 0 &&
                    i18n.t('company:domains.domainManagement.nowNeedToAddHeaderOld', {
                        oldDomains: oldDomainsToUpdate
                    })}
                {newDomainsToAdd == 0 &&
                    oldDomainsToUpdate == 0 &&
                    i18n.t('company:domains.domainManagement.nowNeedToAddHeaderNone', {
                        providerName: domainProvider.value == 'NONE' ? 'DNS' : domainProvider?.label
                    })}

                {site.siteId == 1 && (
                    <>
                        {domainProvider?.kbUrl && (
                            <a
                                data-ph='authenticate_domain_dns_header_guide'
                                href={domainProvider?.kbUrl}
                                target='_blank'
                            >
                                {i18n.t('company:domains.domainManagement.nowNeedToAddLink', {
                                    providerName: domainProvider?.label
                                })}
                            </a>
                        )}
                        {!domainProvider?.kbUrl && (
                            <a
                                data-ph='authenticate_domain_dns_header_guide'
                                href={
                                    'https://transpond.io/support/deliverability-and-compliance/authenticating-your-email-domain'
                                }
                                target='_blank'
                            >
                                {i18n.t('company:domains.domainManagement.nowNeedToAddLink', { providerName: 'DNS' })}
                            </a>
                        )}
                    </>
                )}
            </div>

            <MultiAccordian
                explodedItems={true}
                allowHeaderClick={true}
                items={stepData}
                active={renderedStep}
                setActive={(e) => {
                    if (!e) return
                    setFlowStep(e)
                    setRenderedStep(e)
                }}
            />
        </div>
    )
}
